import type { EstimatedBalance } from '@clarity/contract';

/**
 * Shared, pure estimated-balance aggregation (clarity-daily-update Req 5.5).
 *
 * Task 3.1 shipped the single-branch headline card (`EstimatedBalanceCard`),
 * reading one branch's `EstimatedBalance` straight off the summary. Req 5.5 says
 * the SAME per-branch number must be summable across a brand / all stores by the
 * (separate) `clarity-navigation-scope` views: at a multi-branch scope the figure
 * is a sum of `confirmedOwed` across the in-scope branches, with any branch whose
 * estimate is `unavailable` EXCLUDED from the sum and COUNTED (so the UI can say
 * "+ N branches couldn't be estimated" rather than silently dropping them).
 *
 * This module provides that summable shape + the pure fold, placed in `lib/` so
 * BOTH the single-branch card feature and the future scope views can import it
 * (the card sits in `features/Onboarding.tsx`; the scope views read from
 * `lib/scopeContext.tsx` — a `lib/` helper is the shared home neither owns).
 *
 * It deliberately does NOT render anything: the aggregate's PRESENTATION (the
 * brand/all-stores card) belongs to the scope spec. This spec only exposes the
 * number and the counts in a reusable, best-effort-labelled shape so the scope
 * views consume per-branch numbers uniformly.
 */

/**
 * The result of summing per-branch estimates across a scope — the same
 * best-effort labelling fields as a single {@link EstimatedBalance}, plus the
 * branch tallies the scope views need to caveat the figure.
 *
 * - `confirmedOwed` / `sincePaid` — summed over the `available` branches only
 *   (an `unavailable` branch contributes nothing but is counted below).
 * - `asOf` — the LATEST (most recent) `asOf` among the summed branches, so the
 *   aggregate's staleness reflects the freshest-pinned branch it includes;
 *   `null` when no branch contributed one.
 * - `availableCount` — branches whose estimate was `available` and summed in.
 * - `unavailableCount` — branches whose estimate was `unavailable` (excluded
 *   from the sum, Req 5.5) — the "couldn't be estimated" tally the UI surfaces.
 * - `branchCount` — total branches considered (`available + unavailable`).
 * - `state` — `available` when at least one branch summed in; otherwise
 *   `unavailable` (nothing to show — every branch was unavailable, or the set
 *   was empty), so a caller can treat it exactly like a single estimate.
 */
export interface EstimatedBalanceAggregate {
  state: 'available' | 'unavailable';
  confirmedOwed: number;
  sincePaid: number;
  /**
   * The full "money still coming" headline summed over the `available` branches:
   * each branch's `totalEstimatedOwed` (confirmed + earned-since − ads-since),
   * falling back to its `confirmedOwed` when the branch didn't send a total (an
   * older producer). So a brand/all-stores card shows the same full figure the
   * single-branch card does.
   */
  totalEstimatedOwed: number;
  asOf: string | null;
  availableCount: number;
  unavailableCount: number;
  branchCount: number;
}

/**
 * Fold a set of per-branch estimates into one summable aggregate (Req 5.5).
 *
 * Pure and total: an empty set yields an `unavailable` zero aggregate (nothing
 * to sum), never a fabricated figure — matching how a single estimate reports
 * "unavailable" rather than a bare zero. Non-finite branch amounts are treated
 * defensively as not contributing (guarded like core's estimate), so a bad
 * number can never poison the sum into `NaN`.
 */
export function sumEstimatedBalances(
  estimates: readonly EstimatedBalance[],
): EstimatedBalanceAggregate {
  let confirmedOwed = 0;
  let sincePaid = 0;
  let totalEstimatedOwed = 0;
  let availableCount = 0;
  let unavailableCount = 0;
  let asOf: string | null = null;

  for (const estimate of estimates) {
    // An unavailable branch is excluded from the sum but counted (Req 5.5), so
    // the scope views can caveat the aggregate ("+ N couldn't be estimated").
    if (estimate.state !== 'available') {
      unavailableCount += 1;
      continue;
    }
    // Guard against a non-finite figure poisoning the running sum into NaN —
    // treat it as a branch that couldn't contribute rather than crashing.
    if (!Number.isFinite(estimate.confirmedOwed) || !Number.isFinite(estimate.sincePaid)) {
      unavailableCount += 1;
      continue;
    }
    availableCount += 1;
    confirmedOwed += estimate.confirmedOwed;
    sincePaid += estimate.sincePaid;
    // The full headline, falling back to the confirmed figure for an older
    // branch payload that didn't carry the post-statement total. Guarded so a
    // bad number never poisons the sum.
    const total = estimate.totalEstimatedOwed;
    totalEstimatedOwed +=
      typeof total === 'number' && Number.isFinite(total) && total > 0
        ? total
        : estimate.confirmedOwed;
    // Keep the latest as-of across the summed branches so the aggregate's
    // staleness reflects the freshest branch pinned into it.
    if (estimate.asOf !== null && (asOf === null || estimate.asOf > asOf)) {
      asOf = estimate.asOf;
    }
  }

  return {
    state: availableCount > 0 ? 'available' : 'unavailable',
    confirmedOwed,
    sincePaid,
    totalEstimatedOwed,
    asOf,
    availableCount,
    unavailableCount,
    branchCount: availableCount + unavailableCount,
  };
}
